import { useMemo, useState } from "react";
import { Plus, Search, Users } from "lucide-react";
import { cn } from "lib/utils";
import { FILLED_INPUT } from "shared/tableStyles";
import type { MeetingEmployeeOption } from "../api/MeetingsApi";
import { TONE_TEXT } from "shared/tones";
import OverdueChips, { matchesOverdue } from "./OverdueChips";
import type { OverdueFilter } from "./OverdueChips";
import { formatDaysAgo, formatMeetingDate, initials, overdueTier, sortByNeed } from "./meetingUtils";

interface EmployeePanelProps {
  employees: MeetingEmployeeOption[];
  loading: boolean;
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  overdueFilter: OverdueFilter;
  onOverdueFilterChange: (value: OverdueFilter) => void;
  /** Start a new meeting for this employee; omitted when the viewer cannot create meetings. */
  onLogMeeting?: (employeeId: number) => void;
}

/** Left pane: pick one employee to see their meeting history, or "All employees". */
export default function EmployeePanel({ employees, loading, selectedId, onSelect, overdueFilter, onOverdueFilterChange, onLogMeeting }: EmployeePanelProps) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const visible = useMemo(
    () => sortByNeed(employees)
      .filter((e) => matchesOverdue(overdueFilter, e.last_meeting_date))
      .filter((e) => !q || `${e.name} ${e.designation} ${e.employee_code}`.toLowerCase().includes(q)),
    [employees, q, overdueFilter],
  );

  return (
    <aside className="bg-surface border border-border rounded-xl flex flex-col min-h-0 lg:max-h-[calc(100vh-14rem)]">
      <div className="p-3 border-b border-border">
        <div className="relative">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find employee…"
            aria-label="Find employee"
            className={cn(FILLED_INPUT, "w-full pl-9 pr-3 py-2")}
          />
        </div>
        <div className="mt-2">
          <OverdueChips employees={employees} value={overdueFilter} onChange={onOverdueFilterChange} />
        </div>
      </div>

      <div className="overflow-y-auto p-2 flex-1">
        <button
          type="button"
          onClick={() => onSelect(null)}
          aria-pressed={selectedId === null}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors",
            selectedId === null ? "bg-primary-soft" : "hover:bg-surface-muted",
          )}
        >
          <span className="w-8 h-8 rounded-full bg-surface-muted border border-border flex items-center justify-center shrink-0">
            <Users className="w-4 h-4 text-muted-foreground" />
          </span>
          <span className="flex-1 min-w-0 text-sm font-medium text-foreground">All employees</span>
        </button>

        {loading && employees.length === 0 && (
          <p className="px-3 py-6 text-center text-xs text-muted-foreground">Loading…</p>
        )}
        {!loading && visible.length === 0 && (
          <p className="px-3 py-6 text-center text-xs text-muted-foreground">No employees match “{query}”.</p>
        )}

        {visible.map((e) => {
          const active = e.id === selectedId;
          const tier = overdueTier(e.last_meeting_date);
          return (
            <div key={e.id} className="group relative">
              <button
                type="button"
                onClick={() => onSelect(e.id)}
                aria-pressed={active}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors",
                  active ? "bg-primary-soft" : "hover:bg-surface-muted",
                )}
              >
                <span
                  className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-semibold",
                    active ? "bg-primary text-primary-foreground" : "bg-surface-muted border border-border text-muted-foreground",
                  )}
                >
                  {initials(e.name)}
                </span>
                <span className="flex-1 min-w-0">
                  <span className={cn("block text-sm truncate", active ? "font-semibold text-primary" : "font-medium text-foreground")}>
                    {e.name}
                  </span>
                  <span
                    className={cn("block text-xs truncate", tier === "never" ? TONE_TEXT.danger : tier === "stale" ? TONE_TEXT.warning : "text-muted-foreground")}
                    title={e.last_meeting_date ? `Last meeting ${formatMeetingDate(e.last_meeting_date)}` : undefined}
                  >
                    {e.meeting_count > 0
                      ? `Last: ${formatMeetingDate(e.last_meeting_date, false)} · ${formatDaysAgo(e.last_meeting_date)}`
                      : "No meetings yet"}
                  </span>
                </span>
                {e.meeting_count > 0 && (
                  <span className={cn("text-xs text-muted-foreground tabular-nums", onLogMeeting && "group-hover:invisible group-focus-within:invisible")}>
                    {e.meeting_count}
                  </span>
                )}
              </button>
              {onLogMeeting && (
                <button
                  type="button"
                  onClick={() => onLogMeeting(e.id)}
                  title={`Log a meeting for ${e.name}`}
                  aria-label={`Log a meeting for ${e.name}`}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md flex items-center justify-center text-primary bg-surface border border-border opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                >
                  <Plus className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
}
